import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type ThemeMode = 'light' | 'dark';

interface ThemeContextProps {
  mode: ThemeMode;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

// Constants for localStorage keys to avoid typos
const LOCAL_STORAGE_KEYS = {
  THEME: 'theme',
  PALETTE: 'palette',
} as const;

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  // Helper function to get time-based theme
  const getTimeBasedTheme = (): ThemeMode => {
    const now = new Date();
    const hour = now.getHours();
    // Light mode during day (6 AM to 6 PM), dark mode during night
    return hour >= 6 && hour < 18 ? 'light' : 'dark';
  };

  // Initialize state with localStorage values or smart defaults
  const [mode, setMode] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'light'; // SSR fallback

    const savedTheme = localStorage.getItem(LOCAL_STORAGE_KEYS.THEME);
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      return savedTheme as ThemeMode;
    }

    // Check system preference first
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (systemPrefersDark !== undefined) {
      return systemPrefersDark ? 'dark' : 'light';
    }

    // Fallback to time-based theme
    return getTimeBasedTheme();
  });

  // Apply theme classes to HTML element
  useEffect(() => {
    const html = document.documentElement;

    // Clean up all possible theme classes
    html.classList.remove('light', 'dark');
    // Add current theme classes
    html.classList.add(mode);

    // Persist to localStorage
    localStorage.setItem(LOCAL_STORAGE_KEYS.THEME, mode);
  }, [mode]);

  const toggleMode = useMemo(
    () => () => {
      setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
    },
    []
  );

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      mode,
      toggleMode,
    }),
    [mode, toggleMode]
  );

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};
